import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Button, Input, Avatar, message, Modal } from "antd";
import { SendOutlined, CloseOutlined, ReloadOutlined } from "@ant-design/icons";
import { device, useBreakpoints } from "../../lib/breakpoints";
import { getDebugMode, CLUMSE_AI_DISPLAY_NAME } from "../../helpers/constants";

/**
 * 🤖 CLUMSE AI CHAT WIDGET - AI-Powered Chat Interface
 *
 * Reusable Clumse AI chat widget component for bids, maintenance, and vendor interactions.
 * Features intelligent responses, automated workflows, and seamless integration.
 *
 * @param {Object} props
 * @param {boolean} props.isVisible - Whether the widget should be visible
 * @param {Function} props.fetchInitialMessage - Function to fetch the initial message
 * @param {Function} props.handleSendMessage - Function to handle sending a message
 * @param {Function} props.getChatStorageKey - Function to get localStorage key for chat persistence
 * @param {Function} props.getChatTimestampKey - Function to get localStorage key for timestamp
 * @param {string} props.tooltipText - Text to show in the welcome tooltip
 * @param {string} props.iconPath - Path to icon image for messages/top bar (defaults to clumse-ai.png)
 * @param {string} props.buttonIconPath - Path to icon image for floating button (defaults to clumse-ai-chat.png)
 * @param {string} props.title - Chat title (defaults to "Clumse AI")
 * @param {string} props.subtitle - Chat subtitle
 * @param {string} props.placeholder - Input placeholder text
 * @param {boolean} props.showWelcomeTooltip - Whether to show the welcome tooltip
 * @param {Function} props.onTooltipDismiss - Callback when tooltip is dismissed
 * @param {boolean} props.requireAuth - Whether authentication is required (affects error messages)
 * @param {Function} props.onWidgetToggle - Optional callback when widget is toggled
 * @param {boolean} props.showFullDisclaimer - Whether to show full disclaimer with terms/privacy links (for public listings)
 * @param {Function} props.onReset - Optional callback when chat is reset (for clearing additional storage)
 */
const ClumseAIChatWidget = ({
  isVisible = true,
  fetchInitialMessage,
  handleSendMessage: handleSendMessageProp,
  getChatStorageKey,
  getChatTimestampKey,
  // External trigger: bump this number to force-open the widget.
  openSignal,
  // Optional: after openSignal opens the panel, set the composer to this text
  pendingComposerText,
  // Optional: render non-text messages (cards) inside the chat stream.
  renderCustomMessage,
  // Optional: handle actions emitted by custom message UI
  onCustomAction,
  tooltipText,
  iconPath = "/icons/clumse-ai/clumse-ai.png",
  buttonIconPath = "/icons/clumse-ai/clumse-ai-chat.png",
  title = "Clumse AI",
  subtitle = "AI-Powered Assistant",
  placeholder = "Ask Clumse AI anything...",
  showWelcomeTooltip = false,
  onTooltipDismiss,
  requireAuth = false,
  onWidgetToggle,
  showFullDisclaimer = false,
  onReset,
  // Style customization props (Clumse industrial: slate header, orange accents)
  themeColor = "#000000",
  backgroundColor = "#ffffff",
  textColor = "#1e293b",
  position = "bottom-right", // "bottom-right" | "bottom-left" | "top-right" | "top-left"
}) => {
  const { isMobile } = useBreakpoints();
  
  // Widget state
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(showWelcomeTooltip);
  const [hasInitialMessage, setHasInitialMessage] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  
  // Refs for scroll management
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatContainerRef = useRef(null);
  
  // Storage keys
  const chatStorageKey = getChatStorageKey?.() || "clumseAiChat";
  const timestampStorageKey = getChatTimestampKey?.() || "clumseAiChatTimestamp";

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Load persisted chat messages
  const loadPersistedMessages = () => {
    try {
      const stored = localStorage.getItem(chatStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setMessages(parsed);
          return parsed.length > 0;
        }
      }
    } catch (error) {
      console.error("❌ [CLUMSE-AI-WIDGET] Error loading persisted messages:", error);
    }
    return false;
  };

  // Save messages to localStorage
  const saveMessagesToStorage = (messagesToSave) => {
    try {
      localStorage.setItem(chatStorageKey, JSON.stringify(messagesToSave));
      localStorage.setItem(timestampStorageKey, new Date().toISOString());
    } catch (error) {
      console.error("❌ [CLUMSE-AI-WIDGET] Error saving messages:", error);
    }
  };

  // Add message to chat
  const addMessage = (message) => {
    setMessages(prev => {
      const updated = [...prev, message];
      saveMessagesToStorage(updated);
      return updated;
    });
  };

  // Handle sending a message
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: "user", 
      content: inputValue.trim(),
      timestamp: new Date().toISOString(),
    };

    addMessage(userMessage);
    setInputValue("");
    setIsLoading(true);
    setIsTyping(true);

    try {
      const response = await handleSendMessageProp?.(userMessage.content);
      
      setIsTyping(false);
      
      if (response) {
        const aiMessage = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: response.message || response,
          timestamp: new Date().toISOString(),
          metadata: response.metadata || {},
        };
        
        addMessage(aiMessage);
        
        // Handle custom actions if present
        if (response.actions && onCustomAction) {
          for (const action of response.actions) {
            await onCustomAction(action, { addMessage });
          }
        }
      } else {
        throw new Error("No response received");
      }
    } catch (error) {
      console.error("❌ [CLUMSE-AI-WIDGET] Error sending message:", error);
      setIsTyping(false);
      
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: requireAuth 
          ? "I'm having trouble connecting. Please make sure you're logged in and try again."
          : "I'm experiencing technical difficulties. Please try again in a moment.",
        timestamp: new Date().toISOString(),
        isError: true,
      };
      
      addMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Toggle widget open/closed
  const toggleWidget = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    
    if (newIsOpen) {
      setShowTooltip(false);
      onTooltipDismiss?.();
      
      // Focus input after animation
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
    
    onWidgetToggle?.(newIsOpen);
  };

  // Reset chat
  const handleReset = () => {
    Modal.confirm({
      title: "Clear Chat History",
      content: "Are you sure you want to clear all messages? This action cannot be undone.",
      okText: "Clear",
      cancelText: "Cancel",
      onOk: () => {
        setMessages([]);
        setHasInitialMessage(false);
        localStorage.removeItem(chatStorageKey);
        localStorage.removeItem(timestampStorageKey);
        onReset?.();
        message.success("Chat history cleared");
      },
    });
  };

  // Load initial message
  const loadInitialMessage = async () => {
    if (hasInitialMessage || !fetchInitialMessage) return;

    try {
      setIsLoading(true);
      const response = await fetchInitialMessage();
      
      if (response) {
        const initialMessage = {
          id: "initial",
          role: "assistant",
          content: response.message || response,
          timestamp: new Date().toISOString(),
          isInitial: true,
        };
        
        addMessage(initialMessage);
        setHasInitialMessage(true);
      }
    } catch (error) {
      console.error("❌ [CLUMSE-AI-WIDGET] Error loading initial message:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    const hasPersistedMessages = loadPersistedMessages();
    if (hasPersistedMessages) {
      setHasInitialMessage(true);
    }
  }, []);

  useEffect(() => {
    if (isOpen && !hasInitialMessage && messages.length === 0) {
      loadInitialMessage();
    }
  }, [isOpen, hasInitialMessage, messages.length]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Handle external open signal
  useEffect(() => {
    if (openSignal > 0) {
      setIsOpen(true);
      setShowTooltip(false);
      
      if (pendingComposerText) {
        setInputValue(pendingComposerText);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 300);
      }
    }
  }, [openSignal, pendingComposerText]);

  if (!isVisible) return null;

  return (
    <>
      {/* Chat Widget Container */}
      <WidgetContainer position={position} isOpen={isOpen} isMobile={isMobile}>
        {isOpen && (
          <ChatPanel backgroundColor={backgroundColor} isMobile={isMobile}>
            {/* Header */}
            <ChatHeader themeColor={themeColor}>
              <HeaderContent>
                <Avatar src={iconPath} size="small" />
                <HeaderText>
                  <HeaderTitle>{title}</HeaderTitle>
                  {subtitle && <HeaderSubtitle>{subtitle}</HeaderSubtitle>}
                </HeaderText>
              </HeaderContent>
              <HeaderActions>
                <Button
                  type="text"
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                  size="small"
                  style={{ color: "white" }}
                />
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={toggleWidget}
                  size="small"
                  style={{ color: "white" }}
                />
              </HeaderActions>
            </ChatHeader>

            {/* Messages */}
            <MessagesContainer ref={chatContainerRef} backgroundColor={backgroundColor}>
              {messages.map((msg) => (
                <MessageBubble key={msg.id} isUser={msg.role === "user"}>
                  {msg.role === "assistant" && (
                    <MessageAvatar>
                      <Avatar src={iconPath} size="small" />
                    </MessageAvatar>
                  )}
                  {msg.role === "assistant" ? (
                    <WidgetAssistantColumn>
                      <WidgetAssistantName>
                        {CLUMSE_AI_DISPLAY_NAME}
                      </WidgetAssistantName>
                      <MessageContent
                        isUser={false}
                        isError={msg.isError}
                      >
                        {renderCustomMessage
                          ? renderCustomMessage(msg)
                          : msg.content}
                      </MessageContent>
                    </WidgetAssistantColumn>
                  ) : (
                    <MessageContent
                      isUser={true}
                      isError={msg.isError}
                    >
                      {renderCustomMessage ? renderCustomMessage(msg) : msg.content}
                    </MessageContent>
                  )}
                </MessageBubble>
              ))}
              
              {isTyping && (
                <MessageBubble isUser={false}>
                  <MessageAvatar>
                    <Avatar src={iconPath} size="small" />
                  </MessageAvatar>
                  <WidgetAssistantColumn>
                    <WidgetAssistantName>
                      {CLUMSE_AI_DISPLAY_NAME}
                    </WidgetAssistantName>
                    <TypingIndicator>
                      <TypingDot delay={0} />
                      <TypingDot delay={0.2} />
                      <TypingDot delay={0.4} />
                    </TypingIndicator>
                  </WidgetAssistantColumn>
                </MessageBubble>
              )}
              
              <div ref={messagesEndRef} />
            </MessagesContainer>

            {/* Input */}
            <InputContainer>
              <InputGroup>
                <ChatInput
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onPressEnter={handleKeyPress}
                  placeholder={placeholder}
                  disabled={isLoading}
                  maxLength={1000}
                />
                <SendButton
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSendMessage}
                  loading={isLoading}
                  disabled={!inputValue.trim()}
                />
              </InputGroup>
              
              {showFullDisclaimer && (
                <Disclaimer>
                  By using this chat, you agree to our{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a> and{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </Disclaimer>
              )}
            </InputContainer>
          </ChatPanel>
        )}

        {/* Floating Button */}
        <FloatingButton
          type="primary"
          size="large"
          onClick={toggleWidget}
          $isOpen={isOpen}
        >
          {isOpen ? (
            <CloseOutlined />
          ) : (
            <Avatar src={buttonIconPath} size="small" />
          )}
        </FloatingButton>

        {/* Welcome Tooltip */}
        {showTooltip && !isOpen && (
          <WelcomeTooltip position={position}>
            {tooltipText ||
              "Hi! I'm Clumse AI. Ask about vendors, bids, or site operations."}
            <TooltipClose
              onClick={() => {
                setShowTooltip(false);
                onTooltipDismiss?.();
              }}
            >
              ×
            </TooltipClose>
          </WelcomeTooltip>
        )}
      </WidgetContainer>
    </>
  );
};

// Styled Components
const WidgetContainer = styled.div`
  position: fixed;
  z-index: 1000;
  
  ${props => {
    switch (props.position) {
      case "bottom-left":
        return "bottom: 20px; left: 20px;";
      case "top-right":
        return "top: 20px; right: 20px;";
      case "top-left":
        return "top: 20px; left: 20px;";
      default: // bottom-right
        return "bottom: 20px; right: 20px;";
    }
  }}

  ${props => props.isMobile && `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1001;
  `}
`;

const ChatPanel = styled.div`
  width: 380px;
  height: 500px;
  background: ${(props) => props.backgroundColor};
  border: 3px solid #1e293b;
  border-radius: 0;
  box-shadow: 2px 2px 0 0 #1e293b;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-bottom: 80px;

  ${(props) =>
    props.isMobile &&
    `
    width: 100vw;
    height: 100vh;
    margin-bottom: 0;
  `}

  @media ${device.mobile} {
    width: 100vw;
    height: 100vh;
    margin-bottom: 0;
  }
`;

const ChatHeader = styled.div`
  background: ${(props) => props.themeColor};
  border-bottom: 3px solid #1e293b;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #f8fafc;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderTitle = styled.div`
  font-weight: 800;
  font-size: 16px;
  letter-spacing: -0.02em;
`;

const HeaderSubtitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: rgba(248, 250, 252, 0.78);
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  background: ${props => props.backgroundColor};
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
`;

const MessageBubble = styled.div`
  display: flex;
  margin-bottom: 16px;
  justify-content: ${props => props.isUser ? "flex-end" : "flex-start"};
  align-items: flex-start;
  gap: 8px;
`;

const MessageAvatar = styled.div`
  flex-shrink: 0;
`;

const WidgetAssistantColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  max-width: 80%;
  gap: 4px;
`;

const WidgetAssistantName = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #64748b;
  line-height: 1.2;
`;

const MessageContent = styled.div`
  max-width: ${(props) => (props.isUser ? "80%" : "100%")};
  width: ${(props) => (props.isUser ? "auto" : "100%")};
  min-width: 0;
  padding: 10px 14px;
  border-radius: 0;
  font-size: 14px;
  line-height: 1.45;
  border: 3px solid #1e293b;
  box-shadow: 2px 2px 0 0 #1e293b;

  ${(props) =>
    props.isUser
      ? `
    background: #0f172a;
    color: #f8fafc;
    border-bottom: 3px solid #ff5722;
  `
      : `
    background: ${props.isError ? "#fff2f0" : "#ffffff"};
    color: ${props.isError ? "#e11900" : "#374151"};
    border-color: ${props.isError ? "#e11900" : "#1e293b"};
    box-shadow: ${props.isError ? "3px 3px 0 0 #e11900" : "2px 2px 0 0 #1e293b"};
  `}
`;

const TypingIndicator = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 10px 14px;
  background: #ffffff;
  border: 3px solid #1e293b;
  border-radius: 0;
  box-shadow: 2px 2px 0 0 #1e293b;
`;

const TypingDot = styled.div`
  width: 6px;
  height: 6px;
  background: #1e293b;
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out;
  animation-delay: ${props => props.delay}s;
  
  @keyframes typing {
    0%, 80%, 100% {
      transform: scale(0.8);
      opacity: 0.5;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const InputContainer = styled.div`
  padding: 16px;
  border-top: 3px solid #1e293b;
  background: #ffffff;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const ChatInput = styled(Input.TextArea)`
  flex: 1;
  border-radius: 0;
  min-height: 40px;
  max-height: 120px;
  resize: none;

  .ant-input {
    border: 3px solid #1e293b !important;
    border-radius: 0 !important;
    box-shadow: 2px 2px 0 0 #1e293b !important;
    padding: 10px 14px;
    color: #1e293b;
    font-family: var(--font-primary, "Inter", system-ui, sans-serif);
  }

  .ant-input:focus {
    border-color: #ff5722 !important;
  }
`;

const SendButton = styled(Button)`
  border-radius: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &.ant-btn-primary {
    background: #ff5722 !important;
    border: 2px solid #000000 !important;
    color: #ffffff !important;
    box-shadow: 2px 2px 0 0 #1e293b;
  }

  &.ant-btn-primary:hover {
    background: #f44e1b !important;
    border-color: #000000 !important;
    color: #ffffff !important;
  }
`;

const FloatingButton = styled(Button)`
  width: 56px;
  height: 56px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid #1e293b !important;
  box-shadow: 3px 3px 0 0 #1e293b;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &.ant-btn-primary {
    background: #ff5722 !important;
    border-color: #000000 !important;
  }

  &:hover {
    transform: translate(-1px, -1px);
    box-shadow: 4px 4px 0 0 #1e293b;
  }

  ${(props) =>
    props.$isOpen &&
    `
    transform: rotate(0deg);
  `}
`;

const WelcomeTooltip = styled.div`
  position: absolute;
  ${props => {
    switch (props.position) {
      case "bottom-left":
        return "bottom: 80px; left: 0; transform: translateX(-20px);";
      case "top-right":
        return "top: 80px; right: 0; transform: translateX(20px);";
      case "top-left":
        return "top: 80px; left: 0; transform: translateX(-20px);";
      default: // bottom-right
        return "bottom: 80px; right: 0; transform: translateX(20px);";
    }
  }}
  
  background: white;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  max-width: 250px;
  font-size: 14px;
  line-height: 1.4;
  color: #333;
  
  &::after {
    content: '';
    position: absolute;
    ${props => {
      switch (props.position) {
        case "bottom-left":
          return "bottom: -8px; left: 30px;";
        case "top-right":
          return "top: -8px; right: 30px;";
        case "top-left":
          return "top: -8px; left: 30px;";
        default: // bottom-right
          return "bottom: -8px; right: 30px;";
      }
    }}
    
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    ${props => {
      const isTop = props.position?.startsWith('top');
      return isTop 
        ? "border-bottom: 8px solid white;"
        : "border-top: 8px solid white;";
    }}
  }
`;

const TooltipClose = styled.button`
  position: absolute;
  top: 4px;
  right: 8px;
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: #999;
  
  &:hover {
    color: #333;
  }
`;

const Disclaimer = styled.div`
  font-size: 11px;
  color: #666;
  text-align: center;
  margin-top: 8px;
  line-height: 1.3;
  
  a {
    color: #ff5722;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

// Legacy export for backward compatibility
export const LeoChatWidget = ClumseAIChatWidget;
export default ClumseAIChatWidget;